import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CloseButton from "../closeButton/CloseButton";
import { Align, ButtonColor, Width } from "../utils/types";
import { alignMapping } from "../utils/mappings";
import { mapValue } from "../utils/helpers";
import Button from "../button/Button";
import clsx from "clsx";

interface ModalProps {
  closeButton?: boolean;
  title?: string | React.ReactNode;
  body?: string | React.ReactNode;
  contentPadding?: string; // e.g. "px-[24px] pt-5 pb-4"
  buttonPadding?: string;
  contentContainerClasses?: string; // e.g. "w-full"
  firstButton?: string;
  secondButton?: string;
  alignText?: Align;
  alignContent?: Align;
  alignButton?: Align;
  widthButton?: Width;
  firstButtonColor?: ButtonColor;
  secondButtonColor?: ButtonColor;
  children?: React.ReactNode;
  maxPanelWidth?: string;
  /** When true, omits sm:w-full so maxPanelWidth/panelStyle is not overridden (e.g. for wide modals with custom max-width) */
  disableSmFullWidth?: boolean;
  /** Inline styles for the panel (use when Tailwind arbitrary values don't work, e.g. maxWidth: 'min(1920px, calc(100vw - 110px))') */
  panelStyle?: React.CSSProperties;
  showOverlay?: boolean;
  closeOnOverlayClick?: boolean;
  onFirstButtonClick?: () => void;
  onSecondButtonClick?: () => void;
  onClose?: () => void; // Optional prop for handling modal close action
  headerIcon?: React.ReactNode;
  firstButtonDisabled?: boolean;
  secondButtonDisabled?: boolean;
}

const Modal = ({
  closeButton = false,
  title,
  body,
  contentPadding,
  buttonPadding = "px-[24px] py-[24px]",
  contentContainerClasses,
  firstButton,
  secondButton,
  alignText = "center",
  alignContent,
  alignButton = "center",
  widthButton,
  firstButtonColor = "tertiary",
  secondButtonColor = "tertiary",
  children,
  maxPanelWidth = "max-w-lg",
  disableSmFullWidth = false,
  panelStyle,
  showOverlay,
  closeOnOverlayClick = false,
  onFirstButtonClick,
  onSecondButtonClick,
  onClose,
  headerIcon,
  firstButtonDisabled = false,
  secondButtonDisabled = false,
}: ModalProps) => {
  const [open, setOpen] = useState(true);

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  // tailwind's Dialog will normally call its onClose whenever the close button or its overlay (outside) is clicked
  // we handle overlay clicks here and let the close button handle its own clicks
  const handleOverlayClick = showOverlay && closeOnOverlayClick ? handleClose : () => {};

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="tw-boundary relative z-10" onClose={handleOverlayClick}>
        {/* Important: the z-indexes below are extremely high because another third-party library we use, headlessui-float, sets the z-index of floating menus to 9999, and we want modals to be above all other UI elements */}
        {showOverlay && (
          <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75 z-[15000]" />
        )}
        <div className="fixed inset-0 z-[15001] overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Dialog.Panel
              className={clsx(
                "relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8",
                disableSmFullWidth ? "w-full" : "sm:w-full",
                !panelStyle && maxPanelWidth,
              )}
              style={panelStyle}
            >
              {closeButton && (
                <div className="absolute top-0 right-0 pt-[20px] pr-[24px]">
                  <CloseButton onClick={handleClose} />
                </div>
              )}
              <div
                className={clsx(
                  mapValue<Align, string>(alignMapping, alignText),
                  `flex bg-white`,
                  contentPadding || "px-[24px] pt-5 pb-4",
                )}
              >
                <div className={clsx(closeButton ? "mr-4" : "", contentContainerClasses)}>
                  {headerIcon && <div className="flex justify-center mb-5">{headerIcon}</div>}
                  {title && (
                    <Dialog.Title
                      as="h3"
                      className={clsx(
                        mapValue<Align, string>(alignMapping, alignText),
                        `text-lg leading-6 font-medium text-gray-900`,
                      )}
                    >
                      {title}
                    </Dialog.Title>
                  )}
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{body}</p>
                  </div>
                  {children && (
                    <div
                      className={clsx(
                        "my-4",
                        alignContent === "center" &&
                          "flex w-full flex-col items-center justify-center",
                        alignContent === "left" && "flex w-full justify-start",
                        alignContent === "right" && "flex w-full justify-end",
                      )}
                    >
                      {children}
                    </div>
                  )}
                </div>
              </div>
              {(firstButton || secondButton) && (
                <div
                  className={clsx(
                    mapValue<Align, string>(alignMapping, alignButton),
                    "flex space-x-3",
                    buttonPadding,
                  )}
                >
                  {firstButton && (
                    <Button
                      color={firstButtonColor}
                      width={widthButton}
                      onClick={onFirstButtonClick || handleClose}
                      disabled={firstButtonDisabled}
                    >
                      {firstButton}
                    </Button>
                  )}
                  {secondButton && (
                    <Button
                      color={secondButtonColor}
                      width={widthButton}
                      onClick={onSecondButtonClick || handleClose}
                      disabled={secondButtonDisabled}
                    >
                      {secondButton}
                    </Button>
                  )}
                </div>
              )}
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
